import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { CaretDownIcon, CaretUpIcon, DotsSixVerticalIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';

import type { Json } from '@/integrations/supabase/types';
import { getBlockContent, type Block, type TextBlockContent } from '@/types/database';
import { BlockRenderer } from '@/components/blocks/BlockRenderer';
import { InlineTextBlock } from './InlineTextBlock';

interface EditableBlockProps {
  block: Block;
  basePath: string;
  canManage: boolean;
  selected: boolean;
  isFirstRow: boolean;
  isLastRow: boolean;
  // Row-level move/drag only applies to a block that is alone in its row —
  // moving a block out of a multi-block row isn't supported yet (see the
  // Studio plan's noted follow-up), so those blocks only get delete +
  // add-to-row.
  rowSize: number;
  onSelect: () => void;
  onEdit: () => void;
  onMoveRowUp: () => void;
  onMoveRowDown: () => void;
  onDelete: () => void;
  onAddToRow: () => void;
  onUpdateContent: (content: Json) => void;
}

export function EditableBlock({
  block,
  basePath,
  canManage,
  selected,
  isFirstRow,
  isLastRow,
  rowSize,
  onSelect,
  onEdit,
  onMoveRowUp,
  onMoveRowDown,
  onDelete,
  onAddToRow,
  onUpdateContent,
}: EditableBlockProps) {
  const canDragRow = canManage && rowSize === 1;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    disabled: !canDragRow,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        height: '100%',
        outline: (theme) => (selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent'),
        outlineOffset: -2,
        '&:hover': canManage ? { outline: (theme) => `2px solid ${theme.palette.primary.light}` } : undefined,
        '&:hover .block-toolbar': canManage ? { opacity: 1 } : undefined,
        '&:hover .block-add-affordance': canManage ? { opacity: 1 } : undefined,
        cursor: canManage ? 'pointer' : 'default',
      }}
      onClick={canManage ? onSelect : undefined}
      onKeyDown={
        canManage
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      role={canManage ? 'button' : undefined}
      tabIndex={canManage ? 0 : undefined}
      aria-selected={selected}
    >
      {canManage ? (
        <Stack
          direction="row"
          spacing={0.5}
          className="block-toolbar"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
            p: 0.5,
            opacity: selected ? 1 : 0,
            transition: (theme) => theme.transitions.create('opacity'),
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {canDragRow ? (
            <Tooltip title="Drag to reorder">
              <Box
                component="button"
                type="button"
                {...attributes}
                {...listeners}
                sx={{
                  cursor: 'grab',
                  touchAction: 'none',
                  color: 'text.secondary',
                  border: 'none',
                  bgcolor: 'transparent',
                  display: 'inline-flex',
                  alignItems: 'center',
                  p: 0.5,
                  '&:hover': { color: 'text.primary' },
                }}
                aria-label="Drag to reorder row"
              >
                <DotsSixVerticalIcon size={20} />
              </Box>
            </Tooltip>
          ) : null}
          {canDragRow ? (
            <>
              <Tooltip title="Move row up">
                <span>
                  <IconButton disabled={isFirstRow} onClick={onMoveRowUp} aria-label="Move row up">
                    <CaretUpIcon size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move row down">
                <span>
                  <IconButton disabled={isLastRow} onClick={onMoveRowDown} aria-label="Move row down">
                    <CaretDownIcon size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          ) : null}
          <Tooltip title="Edit">
            <IconButton onClick={onEdit} aria-label="Edit block">
              <PencilSimpleIcon size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete block">
            <IconButton color="error" onClick={onDelete} aria-label="Delete block">
              <TrashIcon size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}

      {canManage ? (
        <Tooltip title="Add block to this row">
          <IconButton
            className="block-add-affordance"
            onClick={(event) => {
              event.stopPropagation();
              onAddToRow();
            }}
            aria-label="Add block to this row"
            sx={{
              position: 'absolute',
              top: '50%',
              right: -16,
              transform: 'translateY(-50%)',
              zIndex: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              opacity: 0,
              transition: (theme) => theme.transitions.create('opacity'),
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <PlusIcon size={16} />
          </IconButton>
        </Tooltip>
      ) : null}

      {block.block_type === 'text' ? (
        <InlineTextBlock
          content={getBlockContent<TextBlockContent>(block)}
          canManage={canManage}
          onCommit={(content) => onUpdateContent(content as unknown as Json)}
        />
      ) : (
        <BlockRenderer block={block} basePath={basePath} />
      )}
    </Box>
  );
}
